import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { ArrowRight, Sparkles, Wand2, BookOpen, Zap } from 'lucide-react'
import { ElfGenerator } from '@/client/components/ElfGenerator'
import { RaceCards } from '@/client/components/RaceCards'
import { Meta } from '@/client/components/Meta'

export function LandingPage() {
  return (
    <div className="flex flex-col">
      <Meta 
        title="Elf Name Generator - Free Fantasy Elf Names"
        description="Looking for the perfect elf name? Generate thousands of fantasy elf names for D&D, RPG, and fiction. High Elf, Dark Elf, Wood Elf & more. Try free now."
        keywords="elf name generator, fantasy elf names, D&D elf names, high elf names, dark elf names, wood elf names, christmas elf names, half elf names, LOTR elf names, RPG character names, female elf names, male elf names, elf name generator for DnD"
      />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden border-b border-border/40">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1441974231531-c6227db76b6e?q=80&w=2560&auto=format&fit=crop')] bg-cover bg-center md:bg-fixed opacity-10 dark:opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background" />
        
        {/* Floating Particles (Animated) */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div 
              key={i}
              className="absolute bg-primary/20 rounded-full blur-xl animate-pulse"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 100 + 50}px`,
                height: `${Math.random() * 100 + 50}px`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${Math.random() * 10 + 10}s`,
              }}
            />
          ))}
        </div>

        <div className="container relative mx-auto max-w-6xl px-4 py-20 text-center">
          <Badge variant="outline" className="mb-6 px-4 py-1 border-primary/30 text-primary bg-primary/5 backdrop-blur-sm">
            <Sparkles className="mr-2 h-3 w-3" />
            #1 Magic Fantasy Name Generator
          </Badge>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-6 leading-[1.1]">
            Generate Your Perfect <br />
            <span className="text-primary bg-clip-text text-transparent bg-gradient-to-r from-primary via-emerald-600 to-primary/80">
              Elf Name
            </span>
          </h1>

          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed font-serif italic">
            "Thousands of unique fantasy elf names for D&D, LOTR, and more. 
            Discover names with ancient meanings and mystical sounds."
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button size="lg" className="rounded-full px-8 h-14 text-lg shadow-xl shadow-primary/20 group" asChild>
              <a href="#generator">
                Start Generating
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
            <Button size="lg" variant="outline" className="rounded-full px-8 h-14 text-lg backdrop-blur-sm" asChild>
              <Link to="/races">Browse Races</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Generator Section */}
      <section id="generator" className="py-24 relative">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Magic Generator</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Select your preferences and let the spirits of the forest guide your naming journey.
            </p>
          </div>
          <ElfGenerator />
        </div>
      </section>

      {/* Popular Names (SEO Section) */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-4">
            <div>
              <h2 className="text-3xl font-bold mb-2">Popular Elf Names</h2>
              <p className="text-muted-foreground">Most searched and loved names by our community.</p>
            </div>
            <Button variant="link" className="group">
              View All Names <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>

          <Tabs defaultValue="high-elf" className="w-full">
            <TabsList className="grid w-full grid-cols-3 md:w-auto md:inline-grid mb-8">
              <TabsTrigger value="high-elf">High Elf</TabsTrigger>
              <TabsTrigger value="dark-elf">Dark Elf</TabsTrigger>
              <TabsTrigger value="wood-elf">Wood Elf</TabsTrigger>
            </TabsList>
            
            <TabsContent value="high-elf">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {['Aelindra', 'Beluar', 'Caladwen', 'Draelor', 'Eldrin', 'Faerand', 'Gaelen', 'Haelon', 'Iorveth', 'Jaelen'].map(name => (
                  <Card key={name} className="hover:bg-accent/50 cursor-pointer border-none shadow-sm transition-colors">
                    <CardContent className="p-4 text-center">
                      <span className="font-bold text-lg">{name}</span>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="dark-elf">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {['Drizzt', 'Jarlaxle', 'Zaknafein', 'Malice', 'Rizzen', 'Vorn', 'Dinin', 'Nalfein', 'Durnad', 'Gromph'].map(name => (
                  <Card key={name} className="hover:bg-accent/50 cursor-pointer border-none shadow-sm transition-colors">
                    <CardContent className="p-4 text-center">
                      <span className="font-bold text-lg">{name}</span>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="wood-elf">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {['Legolas', 'Thranduil', 'Celeborn', 'Haldir', 'Oropher', 'Galion', 'Lindon', 'Erestor', 'Galdor', 'Rohan'].map(name => (
                  <Card key={name} className="hover:bg-accent/50 cursor-pointer border-none shadow-sm transition-colors">
                    <CardContent className="p-4 text-center">
                      <span className="font-bold text-lg">{name}</span>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Race Entry Cards */}
      <section className="py-24">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Explore the Races</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Every lineage has its own unique phonetics and cultural depth.
            </p>
          </div>
          <RaceCards />
        </div>
      </section>

      {/* FAQ Section (SEO) */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto max-w-3xl px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Common Questions</h2>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-left text-lg">What makes a good elf name?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                A good elf name usually incorporates flowing vowel sounds and melodic consonants. For High Elves, names often sound noble and ancient, while Wood Elf names tend to feel more grounded and rhythmic, mirroring the sounds of nature.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-left text-lg">How are elf names structured in D&D?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                In D&D, elves typically have a personal name, a family name, and often a title or nickname. Their names often include prefixes and suffixes derived from elven languages that denote their status, ancestry, or connection to magic.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger className="text-left text-lg">What's the difference between High Elf and Dark Elf names?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                High Elf names are typically bright, airy, and full of soft sounds (e.g., 'Ae', 'El'). Dark Elf (Drow) names tend to be harsher, utilizing sharper consonants like 'Z', 'X', and 'V', reflecting their mysterious and often shadows-bound culture.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Features Grid (Reusing from original but themed) */}
      <section className="py-24">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center text-center p-6 gap-4">
              <div className="bg-primary/10 p-4 rounded-full text-primary">
                <Wand2 className="h-8 w-8" />
              </div>
              <h3 className="font-bold text-xl">10k+ Names</h3>
              <p className="text-muted-foreground text-sm">One of the largest hand-curated databases of elven linguistic roots.</p>
            </div>
            <div className="flex flex-col items-center text-center p-6 gap-4">
              <div className="bg-primary/10 p-4 rounded-full text-primary">
                <BookOpen className="h-8 w-8" />
              </div>
              <h3 className="font-bold text-xl">Meanings Included</h3>
              <p className="text-muted-foreground text-sm">Every name comes with a historical meaning or lore-friendly context.</p>
            </div>
            <div className="flex flex-col items-center text-center p-6 gap-4">
              <div className="bg-primary/10 p-4 rounded-full text-primary">
                <Zap className="h-8 w-8" />
              </div>
              <h3 className="font-bold text-xl">Instant Generation</h3>
              <p className="text-muted-foreground text-sm">Lightning fast generation right in your browser with no delay.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
